import axios, { type AxiosResponse } from 'axios'
import { type SessionOptions } from 'iron-session'
import { useCallback, useContext, useState } from 'react'
import useSWR from 'swr'

import { createUser } from './shopify/graphql/customer'
import { type ErrorMessages } from './helpers'
import { type Locale } from './language'
import { LanguageContext } from './language-context'
import { type ParseResults } from './request'
import { ShopContext } from './shop-context'
import { type UserAddress, type UserProduct } from './user'

export interface SignupFormValues {
  firstName: string
  lastName: string
  email: string
  password: string
}

export interface LoginFormValues {
  email: string
  password: string
}

export interface PasswordRecoveryFormValues {
  email: string
}

export interface StoredUser {
  isLoggedIn: boolean
  email?: string
  token?: string
  addresses?: UserAddress[]
  ownedProducts?: UserProduct[]
}

export interface UserSession {
  user?: StoredUser
}

export type ApiAuthResponse = ParseResults & {
  user?: StoredUser
}

interface LogoutPayload {
  token?: string
}

export const sessionOptions: SessionOptions = {
  password: {
    1: process.env.SECRET_COOKIE_PASSWORD ?? '',
  },
  cookieName: 'nasa_auth',
  cookieOptions: {
    secure: process.env.NEXT_PUBLIC_APP_ENV === 'production',
  },
}

/**
 * User data and mutation hook.
 */
export const useUser = () => {
  const { locale } = useContext(LanguageContext)

  const { data: user, mutate: mutateUser } = useSWR<StoredUser>(
    {
      url: '/api/auth/user',
      localeHeader: locale,
    },
    async ({ url, localeHeader }: { url: string; localeHeader: Locale }) => {
      const response = await axios.get<StoredUser>(url, {
        headers: {
          'X-Locale': localeHeader,
        },
      })

      return response.data
    },
    {
      errorRetryCount: 3,
    },
  )

  return {
    user,
    mutateUser,
  } as const
}

/**
 * Returns a method that logs in a user.
 */
export const useLoginUser = () => {
  const { locale } = useContext(LanguageContext)
  const { mutateUser } = useUser()

  return useCallback(
    async (payload: LoginFormValues) => {
      try {
        const apiLoginResponse = await axios.post<
          ApiAuthResponse,
          AxiosResponse<ApiAuthResponse>,
          LoginFormValues
        >('/api/auth/login', payload, {
          headers: {
            'Content-Type': 'application/json',
            'X-Locale': locale,
          },
        })
        const { user, ...parseResults } = apiLoginResponse.data
        mutateUser(user)

        return parseResults
      } catch (error) {
        console.log(error)

        const parseResults: ParseResults = {
          fieldErrors: {},
          errorCount: 0,
          status: 'unknown_error',
        }
        return parseResults
      }
    },
    [locale, mutateUser],
  )
}

/**
 * Returns a method that logs out a user.
 */
export const useLogoutUser = () => {
  const { user, mutateUser } = useUser()
  const { locale } = useContext(LanguageContext)

  return useCallback(
    async (onSuccess?: () => void) => {
      const payload: LogoutPayload = { token: user?.token }
      const apiLogoutResponse = await axios.post<
        ApiAuthResponse,
        AxiosResponse<ApiAuthResponse>,
        LogoutPayload
      >('/api/auth/logout', payload, {
        headers: {
          'Content-Type': 'application/json',
          'X-Locale': locale,
        },
      })

      // Update SWR cache and redirect to the login page
      mutateUser(apiLogoutResponse.data.user)

      if (onSuccess) {
        onSuccess()
      }
    },
    [locale, mutateUser, user?.token],
  )
}

/**
 * User signup form submit handler hook.
 */
export const useSubmitSignupForm = () => {
  const { locale } = useContext(LanguageContext)
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)
  const loginUser = useLoginUser()

  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})

  const handleSignupSubmit = useCallback(
    async (values: SignupFormValues, onSuccess?: () => void) => {
      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsLoading(true)
      setIsError(false)

      // Create user
      const createUserResult = await createUser(
        locale,
        shopifyStorefrontGraphQlClient,
        values,
      )
      setErrorMessages(createUserResult.fieldErrors)

      if (createUserResult.status !== 'ok') {
        setIsError(true)
      }

      if (
        createUserResult.status === 'ok' &&
        createUserResult.errorCount === 0
      ) {
        const { email, password } = values

        // Login after creating user
        const loginUserResult = await loginUser({
          email,
          password,
        })

        setErrorMessages(loginUserResult.fieldErrors)

        if (loginUserResult.status !== 'ok') {
          setIsError(true)
        }

        if (
          loginUserResult.status === 'ok' &&
          loginUserResult.errorCount === 0 &&
          onSuccess
        ) {
          onSuccess()
        }
      }

      setIsLoading(false)
    },
    [locale, loginUser, shopifyStorefrontGraphQlClient],
  )

  return [isError, isLoading, errorMessages, handleSignupSubmit] as const
}
